"use client";

import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/atoms/card";
import { Calendar, Clock } from "lucide-react";

export interface OfertasCourseCardProps {
  id: string;
  title: string;
  level: string;
  duration: string;
  period: string;
  slug?: string; // optional, we will fallback to id
  applyPrefix?: string; // external application URL prefix
  whiteStyle?: boolean; // when true: white card background and gray apply button
}

export default function CardOfertasFormativas({
  id,
  title,
  level,
  duration,
  period,
  slug,
  applyPrefix,
  whiteStyle = false,
}: OfertasCourseCardProps) {
  const [locale, setLocale] = useState<'pt' | 'en' | 'fr'>('pt');
  useEffect(() => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; locale=`);
    if (parts.length === 2) {
      const v = parts.pop()?.split(';').shift();
      if (v === 'pt' || v === 'en' || v === 'fr') setLocale(v);
    }
  }, []);

  const href = slug ? `/ofertas-formativas/${slug}` : `/ofertas-formativas/${id}`;
  const baseUrl = process.env.NEXT_PUBLIC_OFERTAS_URL || applyPrefix || "";
  const applyUrl = `${baseUrl}/${slug ?? id}`;
  const cardClass = whiteStyle
    ? "bg-white border border-gray-200"
    : "bg-gray-100 border-0";
  const applyBtnClass = whiteStyle
    ? "bg-gray-100 hover:bg-[#82B662]/80 hover:text-white text-[#82B662] hover:text-white"
    : "bg-[#82B662]/20 hover:bg-[#82B662]/80 hover:text-white text-[#82B662] hover:text-white";
  const labels: Record<'pt' | 'en' | 'fr', string> = {
    pt: 'CANDIDATAR',
    en: 'APPLY',
    fr: 'POSTULER',
  };
  const applyLabel = labels[locale] ?? labels.pt;

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on the apply button
    if ((e.target as HTMLElement).closest('a[href^="http"]')) {
      return;
    }
    window.location.href = href;
  };

  return (
    <Card
      className={`${cardClass} rounded-2xl md:rounded-3xl relative pb-6 ml-2 md:pb-0 md:ml-0 h-auto md:h-[12rem] lg:h-[16rem] hover:shadow-lg transition-shadow cursor-pointer`}
      onClick={handleCardClick}
    >
      <CardContent className="px-2 md:px-6 lg:px-8 pt-2 md:pt-4 pb-2 h-full">
        <div className="space-y-3 md:space-y-4 lg:space-y-2">

          {/* Course Title */}
          <div className="min-h-[1.5em] md:min-h-[1.5em] lg:min-h-[3em]">
            <h3 className="text-[#45474F] text-xs md:text-sm lg:text-base font-bold leading-tight font-poppins"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: '1.3',
                wordBreak: 'break-word'
              }}>
              {title}
            </h3>
          </div>

          {/* Course Level */}
          <div className="text-[#377396] font-poppins text-xs md:text-sm lg:text-base font-bold uppercase tracking-wide">
            {`NIVEL ${level}`}
          </div>


          {/* Course Details */}
          <div className="space-y-2 md:space-y-3 lg:space-y-2 mt-4 md:mt-6 lg:mt-8 mb-4 md:mb-6 lg:mb-8 flex gap-2">
            {/* Date */}
            <div className="flex items-baseline gap-2 md:gap-2 lg:gap-3">
              <Calendar className="w-3 h-3 md:w-3 md:h-3 lg:w-4 lg:h-4 text-[#F05A49] flex-shrink-0" />
              <span className="text-[#616161] text-xs md:text-sm lg:text-base">
                {`${duration} Mês(es)`}
              </span>
            </div>

            {/* Period */}
            <div className="flex items-baseline gap-2 md:gap-2 lg:gap-3">
              <Clock className="w-3 h-3 md:w-3 md:h-3 lg:w-4 lg:h-4 text-[#F05A49] flex-shrink-0" />
              <span className="text-[#616161] text-xs md:text-sm lg:text-base">
                {period}
              </span>
            </div>
          </div>

          {/** buttons section */}
          <div className="flex gap-2">
            {/* Apply Button as external link */}
            <a
              href={applyUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className={`border-[#82B662] border-1 text-[#82B662] hover:bg-[#82B662]/80 hover:text-white bg-white inline-flex items-center justify-center w-28 md:w-32 lg:w-40 ${applyBtnClass} font-bold uppercase tracking-wide rounded-full py-2 md:py-3 text-xs md:text-xs lg:text-sm transition-colors`}
            >
              {applyLabel}
            </a>

            {/** detalhes btn */}
            <button
              type="button"
              onClick={handleCardClick}
              className="w-11 h-11 rounded-full flex items-center justify-center border border-[#F05A49] text-[#F05A49] hover:bg-[#F05A49] hover:text-white transition-all hover:scale-110 cursor-pointer"
              aria-label="Ver detalhes da oferta"
            >
              <span className="text-2xl font-normal leading-9">+</span>
            </button>

          </div>


        </div>

      </CardContent>
    </Card>
  );
}
